import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
} from "@mui/material";
import { Base_URL, ApiCall } from "../Common/Path";
import customFailAlert from "../Alerts/customFailAlert";

const RemoveMemberModal = ({
  open,
  onClose,
  member,
  household,
  onMemberRemoved,
  onOwnerChangeRequested,
}) => {
  const [reason, setReason] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);

  const handleClose = () => {
    setReason("");
    setIsRemoving(false);
    onClose();
  };

  const handleRemove = async () => {
    if (!member || !household || isRemoving) return;

    try {
      setIsRemoving(true);

      const body = {
        householdMemberId: member.id,
        reason: reason.trim() || "",
      };

      const url = `${Base_URL}/households/${household.id}/members`;
      const response = await ApiCall(url, body, "delete");
      const statusCode = response?.statusCode || response?.code;

      const ok =
        statusCode === 200 ||
        statusCode === 204 ||
        statusCode === 201 ||
        String(response?.status || "").toUpperCase() === "SUCCESS";

      if (!ok) {
        const errorMessage = response?.message ||
          "Unable to remove member from household. Please try again.";
        customFailAlert(errorMessage);
        if (onMemberRemoved) {
          onMemberRemoved({ success: false, message: errorMessage });
        }
        return;
      }

      if (onMemberRemoved) {
        onMemberRemoved({ success: true, message: "Member removed from household successfully", data: response });
      }

      handleClose();
    } catch (error) {
      console.error("Error removing member:", error);
      const errorMessage = "Something went wrong while removing the member.";
      customFailAlert(errorMessage);
      if (onMemberRemoved) {
        onMemberRemoved({ success: false, message: errorMessage });
      }
    } finally {
      setIsRemoving(false);
    }
  };

  const memberName =
    member?.userInfo?.name ||
    `${member?.userInfo?.firstName || ""} ${member?.userInfo?.lastName || ""}`.trim() ||
    member?.userRole?.user?.email ||
    "this member";

  const householdName = household?.householdName || "the household";

  // Check if the member being removed is the owner
  const isOwner = member?.isOwner === true;
  const members = Array.isArray(household?.members) ? household.members : [];
  // Check for other members (excluding the current member) that are not owners
  // Also check if they have accepted the invitation (can become owner)
  const otherMembers = members.filter(m => {
    if (m.id === member?.id) return false;
    if (m?.isOwner === true) return false;
    // Only include members who have accepted the invitation (can become owner)
    const status = String(m?.status || "").toUpperCase();
    return status === "ACCEPTED";
  });

  // Handle owner change request
  const handleOwnerChangeRequest = () => {
    handleClose();
    if (onOwnerChangeRequested) {
      onOwnerChangeRequested(member);
    }
  };

  // If trying to remove owner and there are no other members, show error
  if (isOwner && otherMembers.length === 0) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#000" }}
          >
            Cannot Remove Owner
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Cannot remove the household owner
            </Typography>
            <Typography variant="body2">
              You cannot remove {memberName} because they are the household owner. 
              A household must have at least one owner. Please add another member first 
              and then change the ownership before removing the current owner.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1D4ED8",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // If trying to remove owner and there are other members, prompt to change owner first
  if (isOwner && otherMembers.length > 0) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#000" }}
          >
            Cannot Remove Household Owner
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Change ownership first
            </Typography>
            <Typography variant="body2">
              You cannot directly remove {memberName} because they are the household owner. 
              Please change the household ownership to another member first, then you can remove the previous owner.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              borderColor: "#D1D5DB",
              color: "#374151",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleOwnerChangeRequest}
            variant="contained"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1D4ED8",
              },
            }}
          >
            Change Owner
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#000" }}
        >
          Remove Member
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Typography
          variant="body1"
          sx={{ color: "#374151", mb: 3 }}
        >
          Are you sure you want to remove {memberName} member from {householdName}?
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason (Optional)"
          placeholder="Enter a reason for removing this member..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isRemoving}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            borderColor: "#D1D5DB",
            color: "#374151",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleRemove}
          variant="contained"
          disabled={isRemoving}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            backgroundColor: isRemoving ? "#E5E7EB" : "#DC2626",
            color: "#FFFFFF",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: isRemoving ? "#E5E7EB" : "#B91C1C",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isRemoving ? "Removing..." : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveMemberModal;

